import { z } from 'zod';

/**
 * КАНОНИЧЕСКИЙ КОНТРАКТ ТОРГОВЫХ ДАННЫХ
 *
 * Оператор в карточке тикета видит покупателя, его заказы и товары. Данные
 * приходят из чужой системы: свой магазин на нашей же платформе продаж,
 * Битрикс, WooCommerce, самописная CRM. У каждой из них своё JSON-поле под
 * одно и то же понятие.
 *
 * Здесь описано то, во что все они приводятся. Правило простое: панель
 * оператора, быстрые действия и подсказки ИИ читают ТОЛЬКО эти формы и
 * никогда — сырой ответ внешней системы. Как только UI начнёт разбирать
 * ответ провайдера напрямую, транспорт останется общим, а интерфейс станет
 * привязан к одному провайдеру — и добавление второго превратится
 * в переписывание панели.
 *
 * Приведение делает `field_mapping` коннектора (см. data-connector.schema).
 * Новый провайдер — это новый пресет маппинга, а не новый код.
 */

/** Ключи возможностей. Коннектор объявляет, какие из них умеет. */
export const COMMERCE_CAPABILITIES = [
  /** Найти покупателя по email / телефону / внешнему id */
  'search_customer',
  /** Заказы конкретного покупателя */
  'customer_orders',
  /** Поиск заказа по номеру / строке */
  'search_order',
  /** Один заказ целиком */
  'get_order',
  /** Поиск товара по названию / артикулу */
  'search_product',
  /** Подборка товаров для этого покупателя (рекомендации) */
  'recommend_products',
] as const;

export type CommerceCapability = (typeof COMMERCE_CAPABILITIES)[number];

export const commerceCapabilitySchema = z.enum(COMMERCE_CAPABILITIES);


/**
 * Текстовое поле из чужого ответа. Битрикс отдаёт id числом, кто-то шлёт
 * пустую строку вместо отсутствия — и то и другое должно стать нормальным
 * `string | null`, а не ошибкой разбора.
 */
const foreignText = z.preprocess(
  (v) => (v === null || v === undefined || v === '' ? null : String(v)),
  z.string().nullable(),
);

/** Обязательный текст (id, название): пусто — значит запись бесполезна. */
const foreignRequiredText = z.preprocess(
  (v) => (v === null || v === undefined ? '' : String(v)),
  z.string().min(1),
);

/**
 * Деньги. Всегда строка: float теряет копейки, а суммы показываются человеку.
 *
 * Схемы ниже намеренно терпимы к типам. Данные приходят из чужой системы:
 * WooCommerce отдаёт сумму строкой, Битрикс — числом, кто-то пришлёт null.
 * Один неудачно заполненный заказ не должен ронять всю панель оператора,
 * поэтому поля приводятся (`coerce`) и падают в `null` (`catch`), а не в
 * исключение.
 */
export const moneySchema = z.object({
  // Только число: пустая сумма (null, '') — не сумма. Раньше она
  // превращалась в строку "null", и оператор видел «старая цена: null».
  // Поля с суммой допускают null (.catch(null)) — туда она и уходит.
  amount: z.preprocess(
    (v) => (typeof v === 'number' ? String(v) : typeof v === 'string' ? v.trim() : v),
    z.string().regex(/^-?\d+(\.\d+)?$/),
  ),
  currency: z.string().min(1).max(8).catch('RUB').default('RUB'),
});
export type Money = z.infer<typeof moneySchema>;

export const commerceCustomerSchema = z.object({
  /** id во внешней системе; может отсутствовать, если та не заводит покупателей */
  external_id: foreignText.default(null),
  name: foreignText.default(null),
  email: foreignText.default(null),
  phone: foreignText.default(null),
  orders_count: z.coerce.number().int().nonnegative().catch(0).default(0),
  total_spent: moneySchema.nullable().catch(null).default(null),
  first_order_at: foreignText.default(null),
  last_order_at: foreignText.default(null),
  /** Метки внешней системы: сегмент, лояльность, «оптовик» */
  tags: z.array(z.coerce.string()).catch([]).default([]),
  /** Ссылка на карточку покупателя во внешней системе (кнопка «открыть») */
  url: foreignText.default(null),
});
export type CommerceCustomer = z.infer<typeof commerceCustomerSchema>;

export const commerceOrderItemSchema = z.object({
  product_external_id: foreignText.default(null),
  title: foreignRequiredText,
  sku: foreignText.default(null),
  quantity: z.coerce.number().catch(1).default(1),
  price: moneySchema.nullable().catch(null).default(null),
  image_url: foreignText.default(null),
});
export type CommerceOrderItem = z.infer<typeof commerceOrderItemSchema>;

/**
 * Статус заказа. Внешние системы называют статусы как угодно; для панели
 * важно лишь, куда красить и что можно предложить клиенту. `status_raw`
 * сохраняется рядом — оператор должен видеть исходное слово продавца.
 */
export const commerceOrderStatusSchema = z.enum([
  'new',
  'paid',
  'processing',
  'shipped',
  'delivered',
  'cancelled',
  'refunded',
  'unknown',
]);
export type CommerceOrderStatus = z.infer<typeof commerceOrderStatusSchema>;

export const commerceOrderSchema = z.object({
  external_id: foreignRequiredText,
  /** Номер, который знает покупатель (может отличаться от id) */
  number: foreignText.default(null),
  status: commerceOrderStatusSchema.catch('unknown').default('unknown'),
  status_raw: foreignText.catch(null).default(null),
  total: moneySchema.nullable().catch(null).default(null),
  created_at: foreignText.default(null),
  items: z.array(commerceOrderItemSchema).catch([]).default([]),
  delivery: z
    .object({
      method: foreignText.default(null),
      address: foreignText.default(null),
      tracking_number: foreignText.default(null),
      tracking_url: foreignText.default(null),
      eta: foreignText.default(null),
    })
    .nullable()
    .default(null),
  payment_method: foreignText.default(null),
  customer: commerceCustomerSchema.partial().nullable().catch(null).default(null),
  url: foreignText.default(null),
});
export type CommerceOrder = z.infer<typeof commerceOrderSchema>;

export const commerceProductSchema = z.object({
  external_id: foreignRequiredText,
  title: foreignRequiredText,
  sku: foreignText.default(null),
  price: moneySchema.nullable().catch(null).default(null),
  compare_at_price: moneySchema.nullable().catch(null).default(null),
  in_stock: z.preprocess(
    (v) =>
      v === null || v === undefined || v === ''
        ? null
        : typeof v === 'string'
          ? ['instock', 'true', '1', 'yes', 'y', 'да'].includes(v.toLowerCase())
          : Boolean(v),
    z.boolean().nullable(),
  ).default(null),
  stock: z.coerce.number().nullable().catch(null).default(null),
  image_url: foreignText.default(null),
  url: foreignText.default(null),
  description: foreignText.default(null),
});
export type CommerceProduct = z.infer<typeof commerceProductSchema>;

/**
 * Что возвращает каждая возможность. Ключ — capability, значение — форма
 * ответа после приведения. Панель оператора переключается по этому же ключу.
 */
export const commerceResultSchemas = {
  search_customer: z.array(commerceCustomerSchema),
  customer_orders: z.array(commerceOrderSchema),
  search_order: z.array(commerceOrderSchema),
  get_order: commerceOrderSchema,
  search_product: z.array(commerceProductSchema),
  recommend_products: z.array(commerceProductSchema),
} as const satisfies Record<CommerceCapability, z.ZodTypeAny>;

/** Форма ответа конкретной возможности (после field_mapping). */
export type CommerceResult<C extends CommerceCapability> = z.infer<
  (typeof commerceResultSchemas)[C]
>;

/** Возможности, отдающие список (панель рисует их одинаково). */
export const COMMERCE_LIST_CAPABILITIES: readonly CommerceCapability[] = [
  'search_customer',
  'customer_orders',
  'search_order',
  'search_product',
  'recommend_products',
];

/**
 * Что кладётся в шаблон быстрого действия. Плоские ключи, потому что шаблон
 * пишет продавец руками: `Ваш заказ {{order.number}} — {{order.status_label}}`.
 */
export const commerceTemplateVarsSchema = z.record(z.string());
export type CommerceTemplateVars = z.infer<typeof commerceTemplateVarsSchema>;

/** Человеческие подписи статусов для шаблонов и панели. */
export const COMMERCE_ORDER_STATUS_LABELS: Record<CommerceOrderStatus, string> = {
  new: 'новый',
  paid: 'оплачен',
  processing: 'собирается',
  shipped: 'отправлен',
  delivered: 'доставлен',
  cancelled: 'отменён',
  refunded: 'возврат',
  unknown: 'уточняется',
};

/**
 * Сведение чужого слова к нашему статусу. Список специально широкий: то же
 * состояние в разных системах называется и по-русски, и по-английски.
 * Не угадали — `unknown`, и оператор видит исходное слово в `status_raw`.
 */
const STATUS_ALIASES: Record<string, CommerceOrderStatus> = {
  new: 'new',
  created: 'new',
  pending: 'new',
  новый: 'new',
  ожидает: 'new',
  paid: 'paid',
  payment_received: 'paid',
  оплачен: 'paid',
  processing: 'processing',
  assembling: 'processing',
  awaiting_shipment: 'processing',
  собирается: 'processing',
  вобработке: 'processing',
  shipped: 'shipped',
  in_transit: 'shipped',
  sent: 'shipped',
  отправлен: 'shipped',
  впути: 'shipped',
  delivered: 'delivered',
  completed: 'delivered',
  done: 'delivered',
  доставлен: 'delivered',
  выполнен: 'delivered',
  cancelled: 'cancelled',
  canceled: 'cancelled',
  отменён: 'cancelled',
  отменен: 'cancelled',
  refunded: 'refunded',
  returned: 'refunded',
  возврат: 'refunded',
};

export function normalizeOrderStatus(raw: string | null | undefined): CommerceOrderStatus {
  if (!raw) return 'unknown';
  const key = raw.trim().toLowerCase().replace(/[\s-]+/g, '');
  return STATUS_ALIASES[key] ?? STATUS_ALIASES[raw.trim().toLowerCase()] ?? 'unknown';
}

// ─── Карточка товара в сообщении ────────────────────────────────────────────

/**
 * Товар, который оператор предложил покупателю прямо в переписке.
 *
 * Лежит в `messages.meta.products`, а в тексте сообщения — замена для
 * каналов, не умеющих карточки (мессенджеры, почта): название, цена,
 * ссылка. Виджет на сайте рисует карточкой. Так одно сообщение правильно
 * выглядит везде, и ни один канал не получает пустоты.
 */
export const PRODUCT_CARDS_META_KEY = 'products';
export const PRODUCT_CARDS_MAX = 10;

/** Ссылка из чужой системы уходит покупателю: только http(s), без javascript: */
const safeHttpUrl = z.preprocess(
  (v) => (typeof v === 'string' && /^https?:\/\//i.test(v.trim()) ? v.trim() : null),
  z.string().max(2000).nullable(),
);

export const productCardSchema = z.object({
  external_id: z.coerce.string().min(1).max(200),
  title: z.string().min(1).max(300),
  price: moneySchema.nullable().catch(null).default(null),
  compare_at_price: moneySchema.nullable().catch(null).default(null),
  image_url: safeHttpUrl.default(null),
  url: safeHttpUrl.default(null),
  in_stock: z.boolean().nullable().catch(null).default(null),
});
export type ProductCard = z.infer<typeof productCardSchema>;

export const productCardsSchema = z.array(productCardSchema).min(1).max(PRODUCT_CARDS_MAX);

/** Текстовая замена карточек для каналов без карточек. */
export function productCardsText(cards: ProductCard[]): string {
  return cards
    .map((c) => {
      const price = c.price ? ` — ${formatMoney(c.price)}` : '';
      return [`${c.title}${price}`, c.url].filter(Boolean).join('\n');
    })
    .join('\n\n');
}

function formatMoney(m: Money): string {
  const n = Number(m.amount);
  const amount = Number.isFinite(n)
    ? n.toLocaleString('ru-RU', { maximumFractionDigits: 2 })
    : m.amount;
  return m.currency === 'RUB' ? `${amount} ₽` : `${amount} ${m.currency}`;
}
